/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  CreateEdgePathParameters,
  CreateFieldMappingPathParameters,
  CreateNodesPathParameters,
  DeleteEdgePathParameters,
  DeleteFieldMappingPathParameters,
  DeleteNexusNodePathParameters,
  DeleteNexusTableFilterPathParameters,
  ExportMappingsPathParameters,
  GetGraphPathParameters,
  GetNexusTableFilterPathParameters,
  GetNodeDataParams,
  GetNodeDataPathParameters,
  GetSchemaImportTemplatePathParameters,
  ImportSchemaPathParameters,
  RemeasureNexusNodeColumnsPathParameters,
  SetNexusTableFilterPathParameters,
  TLBulkActionResponseNexusMappingValidationResult,
  TLHTTPValidationError,
  TLImportSchemaNexusProjectsGraphRequest,
  TLNexusEdgeCreate,
  TLNexusEdgeResponse,
  TLNexusEdgeUpdate,
  TLNexusFieldMappingCreate,
  TLNexusFieldMappingResponse,
  TLNexusFieldMappingUpdate,
  TLNexusGraphResponse,
  TLNexusNodeCreate,
  TLNexusNodeDataResponse,
  TLNexusNodeResponse,
  TLNexusNodeUpdate,
  TLNexusRemeasureResponse,
  TLNexusSchemaImportResponse,
  TLNexusSchemaImportTemplate,
  TLNexusScopeChainInput,
  TLNexusTableFilterResponse,
  TLNexusValidateMappingResponse,
  TLNexusValidateMappingsBulkRequest,
  UpdateEdgePathParameters,
  UpdateFieldMappingPathParameters,
  UpdateNodePathParameters,
  ValidateNexusFieldMappingParams,
  ValidateNexusFieldMappingPathParameters,
  ValidateNexusFieldMappingsBulkPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get Graph
 */
export const getGraph = (
  { projectId }: GetGraphPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusGraphResponse>(
    { url: `/nexus/projects/${projectId}/graph`, method: 'GET', signal },
    options,
  );
};

export const getGetGraphQueryKey = ({ projectId }: GetGraphPathParameters) => {
  return ['nexus', 'projects', projectId, 'graph'] as const;
};

export const getGetGraphQueryOptions = <
  TData = Awaited<ReturnType<typeof getGraph>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: GetGraphPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGraph>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetGraphQueryKey({ projectId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getGraph>>> = ({ signal }) =>
    getGraph({ projectId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getGraph>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetGraphQueryResult = NonNullable<Awaited<ReturnType<typeof getGraph>>>;
export type GetGraphQueryError = ErrorType<TLHTTPValidationError>;

export function useGetGraph<TData = Awaited<ReturnType<typeof getGraph>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetGraphPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGraph>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof getGraph>>, TError, Awaited<ReturnType<typeof getGraph>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetGraph<TData = Awaited<ReturnType<typeof getGraph>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetGraphPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGraph>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<Awaited<ReturnType<typeof getGraph>>, TError, Awaited<ReturnType<typeof getGraph>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetGraph<TData = Awaited<ReturnType<typeof getGraph>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetGraphPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGraph>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Graph
 */

export function useGetGraph<TData = Awaited<ReturnType<typeof getGraph>>, TError = ErrorType<TLHTTPValidationError>>(
  { projectId }: GetGraphPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGraph>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetGraphQueryOptions({ projectId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Graph
 */
export const prefetchGetGraphQuery = async <
  TData = Awaited<ReturnType<typeof getGraph>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: GetGraphPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGraph>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetGraphQueryOptions({ projectId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Graph
 */
export const invalidateGetGraph = async (
  queryClient: QueryClient,
  { projectId }: GetGraphPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetGraphQueryKey({ projectId }) }, options);

  return queryClient;
};

/**
 * @summary Create Edge
 */
export const createEdge = (
  { projectId }: CreateEdgePathParameters,
  tLNexusEdgeCreate: BodyType<TLNexusEdgeCreate>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusEdgeResponse>(
    {
      url: `/nexus/projects/${projectId}/graph/edges`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusEdgeCreate,
      signal,
    },
    options,
  );
};

export const getCreateEdgeQueryKey = (
  { projectId }: CreateEdgePathParameters,
  tLNexusEdgeCreate?: BodyType<TLNexusEdgeCreate>,
) => {
  return ['POST', 'nexus', 'projects', projectId, 'graph', 'edges', tLNexusEdgeCreate] as const;
};

export const getCreateEdgeQueryOptions = <
  TData = Awaited<ReturnType<typeof createEdge>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: CreateEdgePathParameters,
  tLNexusEdgeCreate: BodyType<TLNexusEdgeCreate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createEdge>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateEdgeQueryKey({ projectId }, tLNexusEdgeCreate);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createEdge>>> = ({ signal }) =>
    createEdge({ projectId }, tLNexusEdgeCreate, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof createEdge>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CreateEdgeQueryResult = NonNullable<Awaited<ReturnType<typeof createEdge>>>;
export type CreateEdgeQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateEdge<
  TData = Awaited<ReturnType<typeof createEdge>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateEdgePathParameters,
  tLNexusEdgeCreate: BodyType<TLNexusEdgeCreate>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createEdge>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createEdge>>,
          TError,
          Awaited<ReturnType<typeof createEdge>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateEdge<
  TData = Awaited<ReturnType<typeof createEdge>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateEdgePathParameters,
  tLNexusEdgeCreate: BodyType<TLNexusEdgeCreate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createEdge>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createEdge>>,
          TError,
          Awaited<ReturnType<typeof createEdge>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateEdge<
  TData = Awaited<ReturnType<typeof createEdge>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateEdgePathParameters,
  tLNexusEdgeCreate: BodyType<TLNexusEdgeCreate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createEdge>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Edge
 */

export function useCreateEdge<
  TData = Awaited<ReturnType<typeof createEdge>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: CreateEdgePathParameters,
  tLNexusEdgeCreate: BodyType<TLNexusEdgeCreate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createEdge>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateEdgeQueryOptions({ projectId }, tLNexusEdgeCreate, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Edge
 */
export const prefetchCreateEdgeQuery = async <
  TData = Awaited<ReturnType<typeof createEdge>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: CreateEdgePathParameters,
  tLNexusEdgeCreate: BodyType<TLNexusEdgeCreate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createEdge>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateEdgeQueryOptions({ projectId }, tLNexusEdgeCreate, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Edge
 */
export const invalidateCreateEdge = async (
  queryClient: QueryClient,
  { projectId }: CreateEdgePathParameters,
  tLNexusEdgeCreate: BodyType<TLNexusEdgeCreate>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateEdgeQueryKey({ projectId }, tLNexusEdgeCreate) }, options);

  return queryClient;
};

/**
 * @summary Delete Edge
 */
export const deleteEdge = (
  { projectId, edgeId }: DeleteEdgePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>(
    { url: `/nexus/projects/${projectId}/graph/edges/${edgeId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteEdgeQueryKey = ({ projectId, edgeId }: DeleteEdgePathParameters) => {
  return ['DELETE', 'nexus', 'projects', projectId, 'graph', 'edges', edgeId] as const;
};

export const getDeleteEdgeQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteEdge>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, edgeId }: DeleteEdgePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteEdge>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteEdgeQueryKey({ projectId, edgeId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteEdge>>> = ({ signal }) =>
    deleteEdge({ projectId, edgeId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && edgeId !== null && edgeId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteEdge>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteEdgeQueryResult = NonNullable<Awaited<ReturnType<typeof deleteEdge>>>;
export type DeleteEdgeQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteEdge<
  TData = Awaited<ReturnType<typeof deleteEdge>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteEdgePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteEdge>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteEdge>>,
          TError,
          Awaited<ReturnType<typeof deleteEdge>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteEdge<
  TData = Awaited<ReturnType<typeof deleteEdge>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteEdgePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteEdge>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteEdge>>,
          TError,
          Awaited<ReturnType<typeof deleteEdge>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteEdge<
  TData = Awaited<ReturnType<typeof deleteEdge>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteEdgePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteEdge>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Edge
 */

export function useDeleteEdge<
  TData = Awaited<ReturnType<typeof deleteEdge>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, edgeId }: DeleteEdgePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteEdge>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteEdgeQueryOptions({ projectId, edgeId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Edge
 */
export const prefetchDeleteEdgeQuery = async <
  TData = Awaited<ReturnType<typeof deleteEdge>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, edgeId }: DeleteEdgePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteEdge>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteEdgeQueryOptions({ projectId, edgeId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Edge
 */
export const invalidateDeleteEdge = async (
  queryClient: QueryClient,
  { projectId, edgeId }: DeleteEdgePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteEdgeQueryKey({ projectId, edgeId }) }, options);

  return queryClient;
};

/**
 * @summary Update Edge
 */
export const updateEdge = (
  { projectId, edgeId }: UpdateEdgePathParameters,
  tLNexusEdgeUpdate: BodyType<TLNexusEdgeUpdate>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusEdgeResponse>(
    {
      url: `/nexus/projects/${projectId}/graph/edges/${edgeId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusEdgeUpdate,
      signal,
    },
    options,
  );
};

export const getUpdateEdgeQueryKey = (
  { projectId, edgeId }: UpdateEdgePathParameters,
  tLNexusEdgeUpdate?: BodyType<TLNexusEdgeUpdate>,
) => {
  return ['PATCH', 'nexus', 'projects', projectId, 'graph', 'edges', edgeId, tLNexusEdgeUpdate] as const;
};

export const getUpdateEdgeQueryOptions = <
  TData = Awaited<ReturnType<typeof updateEdge>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, edgeId }: UpdateEdgePathParameters,
  tLNexusEdgeUpdate: BodyType<TLNexusEdgeUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateEdge>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateEdgeQueryKey({ projectId, edgeId }, tLNexusEdgeUpdate);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateEdge>>> = ({ signal }) =>
    updateEdge({ projectId, edgeId }, tLNexusEdgeUpdate, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && edgeId !== null && edgeId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateEdge>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateEdgeQueryResult = NonNullable<Awaited<ReturnType<typeof updateEdge>>>;
export type UpdateEdgeQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateEdge<
  TData = Awaited<ReturnType<typeof updateEdge>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateEdgePathParameters,
  tLNexusEdgeUpdate: BodyType<TLNexusEdgeUpdate>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateEdge>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateEdge>>,
          TError,
          Awaited<ReturnType<typeof updateEdge>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateEdge<
  TData = Awaited<ReturnType<typeof updateEdge>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateEdgePathParameters,
  tLNexusEdgeUpdate: BodyType<TLNexusEdgeUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateEdge>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateEdge>>,
          TError,
          Awaited<ReturnType<typeof updateEdge>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateEdge<
  TData = Awaited<ReturnType<typeof updateEdge>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateEdgePathParameters,
  tLNexusEdgeUpdate: BodyType<TLNexusEdgeUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateEdge>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Edge
 */

export function useUpdateEdge<
  TData = Awaited<ReturnType<typeof updateEdge>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, edgeId }: UpdateEdgePathParameters,
  tLNexusEdgeUpdate: BodyType<TLNexusEdgeUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateEdge>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateEdgeQueryOptions({ projectId, edgeId }, tLNexusEdgeUpdate, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Edge
 */
export const prefetchUpdateEdgeQuery = async <
  TData = Awaited<ReturnType<typeof updateEdge>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, edgeId }: UpdateEdgePathParameters,
  tLNexusEdgeUpdate: BodyType<TLNexusEdgeUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateEdge>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateEdgeQueryOptions({ projectId, edgeId }, tLNexusEdgeUpdate, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Edge
 */
export const invalidateUpdateEdge = async (
  queryClient: QueryClient,
  { projectId, edgeId }: UpdateEdgePathParameters,
  tLNexusEdgeUpdate: BodyType<TLNexusEdgeUpdate>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateEdgeQueryKey({ projectId, edgeId }, tLNexusEdgeUpdate) },
    options,
  );

  return queryClient;
};

/**
 * @summary Export Mappings
 */
export const exportMappings = (
  { projectId }: ExportMappingsPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<unknown>({ url: `/nexus/projects/${projectId}/graph/export`, method: 'GET', signal }, options);
};

export const getExportMappingsQueryKey = ({ projectId }: ExportMappingsPathParameters) => {
  return ['nexus', 'projects', projectId, 'graph', 'export'] as const;
};

export const getExportMappingsQueryOptions = <
  TData = Awaited<ReturnType<typeof exportMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: ExportMappingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getExportMappingsQueryKey({ projectId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof exportMappings>>> = ({ signal }) =>
    exportMappings({ projectId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof exportMappings>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ExportMappingsQueryResult = NonNullable<Awaited<ReturnType<typeof exportMappings>>>;
export type ExportMappingsQueryError = ErrorType<TLHTTPValidationError>;

export function useExportMappings<
  TData = Awaited<ReturnType<typeof exportMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportMappingsPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportMappings>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportMappings>>,
          TError,
          Awaited<ReturnType<typeof exportMappings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExportMappings<
  TData = Awaited<ReturnType<typeof exportMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportMappingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportMappings>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportMappings>>,
          TError,
          Awaited<ReturnType<typeof exportMappings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExportMappings<
  TData = Awaited<ReturnType<typeof exportMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportMappingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Export Mappings
 */

export function useExportMappings<
  TData = Awaited<ReturnType<typeof exportMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: ExportMappingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getExportMappingsQueryOptions({ projectId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Export Mappings
 */
export const prefetchExportMappingsQuery = async <
  TData = Awaited<ReturnType<typeof exportMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: ExportMappingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getExportMappingsQueryOptions({ projectId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Export Mappings
 */
export const invalidateExportMappings = async (
  queryClient: QueryClient,
  { projectId }: ExportMappingsPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getExportMappingsQueryKey({ projectId }) }, options);

  return queryClient;
};

/**
 * @summary Create Field Mapping
 */
export const createFieldMapping = (
  { projectId }: CreateFieldMappingPathParameters,
  tLNexusFieldMappingCreate: BodyType<TLNexusFieldMappingCreate>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusFieldMappingResponse>(
    {
      url: `/nexus/projects/${projectId}/graph/field-mappings`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusFieldMappingCreate,
      signal,
    },
    options,
  );
};

export const getCreateFieldMappingQueryKey = (
  { projectId }: CreateFieldMappingPathParameters,
  tLNexusFieldMappingCreate?: BodyType<TLNexusFieldMappingCreate>,
) => {
  return ['POST', 'nexus', 'projects', projectId, 'graph', 'field-mappings', tLNexusFieldMappingCreate] as const;
};

export const getCreateFieldMappingQueryOptions = <
  TData = Awaited<ReturnType<typeof createFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: CreateFieldMappingPathParameters,
  tLNexusFieldMappingCreate: BodyType<TLNexusFieldMappingCreate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createFieldMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateFieldMappingQueryKey({ projectId }, tLNexusFieldMappingCreate);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createFieldMapping>>> = ({ signal }) =>
    createFieldMapping({ projectId }, tLNexusFieldMappingCreate, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof createFieldMapping>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CreateFieldMappingQueryResult = NonNullable<Awaited<ReturnType<typeof createFieldMapping>>>;
export type CreateFieldMappingQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateFieldMapping<
  TData = Awaited<ReturnType<typeof createFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateFieldMappingPathParameters,
  tLNexusFieldMappingCreate: BodyType<TLNexusFieldMappingCreate>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createFieldMapping>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createFieldMapping>>,
          TError,
          Awaited<ReturnType<typeof createFieldMapping>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateFieldMapping<
  TData = Awaited<ReturnType<typeof createFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateFieldMappingPathParameters,
  tLNexusFieldMappingCreate: BodyType<TLNexusFieldMappingCreate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createFieldMapping>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createFieldMapping>>,
          TError,
          Awaited<ReturnType<typeof createFieldMapping>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateFieldMapping<
  TData = Awaited<ReturnType<typeof createFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateFieldMappingPathParameters,
  tLNexusFieldMappingCreate: BodyType<TLNexusFieldMappingCreate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createFieldMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Field Mapping
 */

export function useCreateFieldMapping<
  TData = Awaited<ReturnType<typeof createFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: CreateFieldMappingPathParameters,
  tLNexusFieldMappingCreate: BodyType<TLNexusFieldMappingCreate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createFieldMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateFieldMappingQueryOptions({ projectId }, tLNexusFieldMappingCreate, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Field Mapping
 */
export const prefetchCreateFieldMappingQuery = async <
  TData = Awaited<ReturnType<typeof createFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: CreateFieldMappingPathParameters,
  tLNexusFieldMappingCreate: BodyType<TLNexusFieldMappingCreate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createFieldMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateFieldMappingQueryOptions({ projectId }, tLNexusFieldMappingCreate, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Field Mapping
 */
export const invalidateCreateFieldMapping = async (
  queryClient: QueryClient,
  { projectId }: CreateFieldMappingPathParameters,
  tLNexusFieldMappingCreate: BodyType<TLNexusFieldMappingCreate>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateFieldMappingQueryKey({ projectId }, tLNexusFieldMappingCreate) },
    options,
  );

  return queryClient;
};

/**
 * @summary Validate Nexus Field Mappings Bulk
 */
export const validateNexusFieldMappingsBulk = (
  { projectId }: ValidateNexusFieldMappingsBulkPathParameters,
  tLNexusValidateMappingsBulkRequest: BodyType<TLNexusValidateMappingsBulkRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLBulkActionResponseNexusMappingValidationResult>(
    {
      url: `/nexus/projects/${projectId}/graph/field-mappings/validate-bulk`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusValidateMappingsBulkRequest,
      signal,
    },
    options,
  );
};

export const getValidateNexusFieldMappingsBulkQueryKey = (
  { projectId }: ValidateNexusFieldMappingsBulkPathParameters,
  tLNexusValidateMappingsBulkRequest?: BodyType<TLNexusValidateMappingsBulkRequest>,
) => {
  return [
    'POST',
    'nexus',
    'projects',
    projectId,
    'graph',
    'field-mappings',
    'validate-bulk',
    tLNexusValidateMappingsBulkRequest,
  ] as const;
};

export const getValidateNexusFieldMappingsBulkQueryOptions = <
  TData = Awaited<ReturnType<typeof validateNexusFieldMappingsBulk>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: ValidateNexusFieldMappingsBulkPathParameters,
  tLNexusValidateMappingsBulkRequest: BodyType<TLNexusValidateMappingsBulkRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateNexusFieldMappingsBulk>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getValidateNexusFieldMappingsBulkQueryKey({ projectId }, tLNexusValidateMappingsBulkRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof validateNexusFieldMappingsBulk>>> = ({ signal }) =>
    validateNexusFieldMappingsBulk({ projectId }, tLNexusValidateMappingsBulkRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof validateNexusFieldMappingsBulk>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ValidateNexusFieldMappingsBulkQueryResult = NonNullable<
  Awaited<ReturnType<typeof validateNexusFieldMappingsBulk>>
>;
export type ValidateNexusFieldMappingsBulkQueryError = ErrorType<TLHTTPValidationError>;

export function useValidateNexusFieldMappingsBulk<
  TData = Awaited<ReturnType<typeof validateNexusFieldMappingsBulk>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ValidateNexusFieldMappingsBulkPathParameters,
  tLNexusValidateMappingsBulkRequest: BodyType<TLNexusValidateMappingsBulkRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateNexusFieldMappingsBulk>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof validateNexusFieldMappingsBulk>>,
          TError,
          Awaited<ReturnType<typeof validateNexusFieldMappingsBulk>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useValidateNexusFieldMappingsBulk<
  TData = Awaited<ReturnType<typeof validateNexusFieldMappingsBulk>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ValidateNexusFieldMappingsBulkPathParameters,
  tLNexusValidateMappingsBulkRequest: BodyType<TLNexusValidateMappingsBulkRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateNexusFieldMappingsBulk>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof validateNexusFieldMappingsBulk>>,
          TError,
          Awaited<ReturnType<typeof validateNexusFieldMappingsBulk>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useValidateNexusFieldMappingsBulk<
  TData = Awaited<ReturnType<typeof validateNexusFieldMappingsBulk>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ValidateNexusFieldMappingsBulkPathParameters,
  tLNexusValidateMappingsBulkRequest: BodyType<TLNexusValidateMappingsBulkRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateNexusFieldMappingsBulk>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Validate Nexus Field Mappings Bulk
 */

export function useValidateNexusFieldMappingsBulk<
  TData = Awaited<ReturnType<typeof validateNexusFieldMappingsBulk>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: ValidateNexusFieldMappingsBulkPathParameters,
  tLNexusValidateMappingsBulkRequest: BodyType<TLNexusValidateMappingsBulkRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateNexusFieldMappingsBulk>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getValidateNexusFieldMappingsBulkQueryOptions(
    { projectId },
    tLNexusValidateMappingsBulkRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Validate Nexus Field Mappings Bulk
 */
export const prefetchValidateNexusFieldMappingsBulkQuery = async <
  TData = Awaited<ReturnType<typeof validateNexusFieldMappingsBulk>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: ValidateNexusFieldMappingsBulkPathParameters,
  tLNexusValidateMappingsBulkRequest: BodyType<TLNexusValidateMappingsBulkRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateNexusFieldMappingsBulk>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getValidateNexusFieldMappingsBulkQueryOptions(
    { projectId },
    tLNexusValidateMappingsBulkRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Validate Nexus Field Mappings Bulk
 */
export const invalidateValidateNexusFieldMappingsBulk = async (
  queryClient: QueryClient,
  { projectId }: ValidateNexusFieldMappingsBulkPathParameters,
  tLNexusValidateMappingsBulkRequest: BodyType<TLNexusValidateMappingsBulkRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getValidateNexusFieldMappingsBulkQueryKey({ projectId }, tLNexusValidateMappingsBulkRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Delete Field Mapping
 */
export const deleteFieldMapping = (
  { projectId, mappingId }: DeleteFieldMappingPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>(
    { url: `/nexus/projects/${projectId}/graph/field-mappings/${mappingId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteFieldMappingQueryKey = ({ projectId, mappingId }: DeleteFieldMappingPathParameters) => {
  return ['DELETE', 'nexus', 'projects', projectId, 'graph', 'field-mappings', mappingId] as const;
};

export const getDeleteFieldMappingQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, mappingId }: DeleteFieldMappingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteFieldMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteFieldMappingQueryKey({ projectId, mappingId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteFieldMapping>>> = ({ signal }) =>
    deleteFieldMapping({ projectId, mappingId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && mappingId !== null && mappingId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteFieldMapping>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteFieldMappingQueryResult = NonNullable<Awaited<ReturnType<typeof deleteFieldMapping>>>;
export type DeleteFieldMappingQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteFieldMapping<
  TData = Awaited<ReturnType<typeof deleteFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteFieldMappingPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteFieldMapping>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteFieldMapping>>,
          TError,
          Awaited<ReturnType<typeof deleteFieldMapping>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteFieldMapping<
  TData = Awaited<ReturnType<typeof deleteFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteFieldMappingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteFieldMapping>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteFieldMapping>>,
          TError,
          Awaited<ReturnType<typeof deleteFieldMapping>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteFieldMapping<
  TData = Awaited<ReturnType<typeof deleteFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteFieldMappingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteFieldMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Field Mapping
 */

export function useDeleteFieldMapping<
  TData = Awaited<ReturnType<typeof deleteFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, mappingId }: DeleteFieldMappingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteFieldMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteFieldMappingQueryOptions({ projectId, mappingId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Field Mapping
 */
export const prefetchDeleteFieldMappingQuery = async <
  TData = Awaited<ReturnType<typeof deleteFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, mappingId }: DeleteFieldMappingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteFieldMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteFieldMappingQueryOptions({ projectId, mappingId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Field Mapping
 */
export const invalidateDeleteFieldMapping = async (
  queryClient: QueryClient,
  { projectId, mappingId }: DeleteFieldMappingPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteFieldMappingQueryKey({ projectId, mappingId }) }, options);

  return queryClient;
};

/**
 * @summary Update Field Mapping
 */
export const updateFieldMapping = (
  { projectId, mappingId }: UpdateFieldMappingPathParameters,
  tLNexusFieldMappingUpdate: BodyType<TLNexusFieldMappingUpdate>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusFieldMappingResponse>(
    {
      url: `/nexus/projects/${projectId}/graph/field-mappings/${mappingId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusFieldMappingUpdate,
      signal,
    },
    options,
  );
};

export const getUpdateFieldMappingQueryKey = (
  { projectId, mappingId }: UpdateFieldMappingPathParameters,
  tLNexusFieldMappingUpdate?: BodyType<TLNexusFieldMappingUpdate>,
) => {
  return [
    'PATCH',
    'nexus',
    'projects',
    projectId,
    'graph',
    'field-mappings',
    mappingId,
    tLNexusFieldMappingUpdate,
  ] as const;
};

export const getUpdateFieldMappingQueryOptions = <
  TData = Awaited<ReturnType<typeof updateFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, mappingId }: UpdateFieldMappingPathParameters,
  tLNexusFieldMappingUpdate: BodyType<TLNexusFieldMappingUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFieldMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getUpdateFieldMappingQueryKey({ projectId, mappingId }, tLNexusFieldMappingUpdate);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateFieldMapping>>> = ({ signal }) =>
    updateFieldMapping({ projectId, mappingId }, tLNexusFieldMappingUpdate, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && mappingId !== null && mappingId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateFieldMapping>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateFieldMappingQueryResult = NonNullable<Awaited<ReturnType<typeof updateFieldMapping>>>;
export type UpdateFieldMappingQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateFieldMapping<
  TData = Awaited<ReturnType<typeof updateFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateFieldMappingPathParameters,
  tLNexusFieldMappingUpdate: BodyType<TLNexusFieldMappingUpdate>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFieldMapping>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateFieldMapping>>,
          TError,
          Awaited<ReturnType<typeof updateFieldMapping>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateFieldMapping<
  TData = Awaited<ReturnType<typeof updateFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateFieldMappingPathParameters,
  tLNexusFieldMappingUpdate: BodyType<TLNexusFieldMappingUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFieldMapping>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateFieldMapping>>,
          TError,
          Awaited<ReturnType<typeof updateFieldMapping>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateFieldMapping<
  TData = Awaited<ReturnType<typeof updateFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateFieldMappingPathParameters,
  tLNexusFieldMappingUpdate: BodyType<TLNexusFieldMappingUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFieldMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Field Mapping
 */

export function useUpdateFieldMapping<
  TData = Awaited<ReturnType<typeof updateFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, mappingId }: UpdateFieldMappingPathParameters,
  tLNexusFieldMappingUpdate: BodyType<TLNexusFieldMappingUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFieldMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateFieldMappingQueryOptions({ projectId, mappingId }, tLNexusFieldMappingUpdate, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Field Mapping
 */
export const prefetchUpdateFieldMappingQuery = async <
  TData = Awaited<ReturnType<typeof updateFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, mappingId }: UpdateFieldMappingPathParameters,
  tLNexusFieldMappingUpdate: BodyType<TLNexusFieldMappingUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFieldMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateFieldMappingQueryOptions({ projectId, mappingId }, tLNexusFieldMappingUpdate, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Field Mapping
 */
export const invalidateUpdateFieldMapping = async (
  queryClient: QueryClient,
  { projectId, mappingId }: UpdateFieldMappingPathParameters,
  tLNexusFieldMappingUpdate: BodyType<TLNexusFieldMappingUpdate>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateFieldMappingQueryKey({ projectId, mappingId }, tLNexusFieldMappingUpdate) },
    options,
  );

  return queryClient;
};

/**
 * @summary Validate Nexus Field Mapping
 */
export const validateNexusFieldMapping = (
  { projectId, mappingId }: ValidateNexusFieldMappingPathParameters,
  params?: ValidateNexusFieldMappingParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusValidateMappingResponse>(
    { url: `/nexus/projects/${projectId}/graph/field-mappings/${mappingId}/validate`, method: 'POST', params, signal },
    options,
  );
};

export const getValidateNexusFieldMappingQueryKey = (
  { projectId, mappingId }: ValidateNexusFieldMappingPathParameters,
  params?: ValidateNexusFieldMappingParams,
) => {
  return [
    'POST',
    'nexus',
    'projects',
    projectId,
    'graph',
    'field-mappings',
    mappingId,
    'validate',
    ...(params ? [params] : []),
  ] as const;
};

export const getValidateNexusFieldMappingQueryOptions = <
  TData = Awaited<ReturnType<typeof validateNexusFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, mappingId }: ValidateNexusFieldMappingPathParameters,
  params?: ValidateNexusFieldMappingParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateNexusFieldMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getValidateNexusFieldMappingQueryKey({ projectId, mappingId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof validateNexusFieldMapping>>> = ({ signal }) =>
    validateNexusFieldMapping({ projectId, mappingId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && mappingId !== null && mappingId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof validateNexusFieldMapping>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ValidateNexusFieldMappingQueryResult = NonNullable<Awaited<ReturnType<typeof validateNexusFieldMapping>>>;
export type ValidateNexusFieldMappingQueryError = ErrorType<TLHTTPValidationError>;

export function useValidateNexusFieldMapping<
  TData = Awaited<ReturnType<typeof validateNexusFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ValidateNexusFieldMappingPathParameters,
  params: undefined | ValidateNexusFieldMappingParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateNexusFieldMapping>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof validateNexusFieldMapping>>,
          TError,
          Awaited<ReturnType<typeof validateNexusFieldMapping>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useValidateNexusFieldMapping<
  TData = Awaited<ReturnType<typeof validateNexusFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ValidateNexusFieldMappingPathParameters,
  params?: ValidateNexusFieldMappingParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateNexusFieldMapping>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof validateNexusFieldMapping>>,
          TError,
          Awaited<ReturnType<typeof validateNexusFieldMapping>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useValidateNexusFieldMapping<
  TData = Awaited<ReturnType<typeof validateNexusFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ValidateNexusFieldMappingPathParameters,
  params?: ValidateNexusFieldMappingParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateNexusFieldMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Validate Nexus Field Mapping
 */

export function useValidateNexusFieldMapping<
  TData = Awaited<ReturnType<typeof validateNexusFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, mappingId }: ValidateNexusFieldMappingPathParameters,
  params?: ValidateNexusFieldMappingParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateNexusFieldMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getValidateNexusFieldMappingQueryOptions({ projectId, mappingId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Validate Nexus Field Mapping
 */
export const prefetchValidateNexusFieldMappingQuery = async <
  TData = Awaited<ReturnType<typeof validateNexusFieldMapping>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, mappingId }: ValidateNexusFieldMappingPathParameters,
  params?: ValidateNexusFieldMappingParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateNexusFieldMapping>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getValidateNexusFieldMappingQueryOptions({ projectId, mappingId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Validate Nexus Field Mapping
 */
export const invalidateValidateNexusFieldMapping = async (
  queryClient: QueryClient,
  { projectId, mappingId }: ValidateNexusFieldMappingPathParameters,
  params?: ValidateNexusFieldMappingParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getValidateNexusFieldMappingQueryKey({ projectId, mappingId }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Create Nodes
 */
export const createNodes = (
  { projectId }: CreateNodesPathParameters,
  tLNexusNodeCreate: BodyType<TLNexusNodeCreate[]>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusNodeResponse[]>(
    {
      url: `/nexus/projects/${projectId}/graph/nodes`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusNodeCreate,
      signal,
    },
    options,
  );
};

export const getCreateNodesQueryKey = (
  { projectId }: CreateNodesPathParameters,
  tLNexusNodeCreate?: BodyType<TLNexusNodeCreate[]>,
) => {
  return ['POST', 'nexus', 'projects', projectId, 'graph', 'nodes', tLNexusNodeCreate] as const;
};

export const getCreateNodesQueryOptions = <
  TData = Awaited<ReturnType<typeof createNodes>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: CreateNodesPathParameters,
  tLNexusNodeCreate: BodyType<TLNexusNodeCreate[]>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createNodes>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateNodesQueryKey({ projectId }, tLNexusNodeCreate);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createNodes>>> = ({ signal }) =>
    createNodes({ projectId }, tLNexusNodeCreate, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof createNodes>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CreateNodesQueryResult = NonNullable<Awaited<ReturnType<typeof createNodes>>>;
export type CreateNodesQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateNodes<
  TData = Awaited<ReturnType<typeof createNodes>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateNodesPathParameters,
  tLNexusNodeCreate: BodyType<TLNexusNodeCreate[]>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createNodes>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createNodes>>,
          TError,
          Awaited<ReturnType<typeof createNodes>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateNodes<
  TData = Awaited<ReturnType<typeof createNodes>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateNodesPathParameters,
  tLNexusNodeCreate: BodyType<TLNexusNodeCreate[]>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createNodes>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createNodes>>,
          TError,
          Awaited<ReturnType<typeof createNodes>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateNodes<
  TData = Awaited<ReturnType<typeof createNodes>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateNodesPathParameters,
  tLNexusNodeCreate: BodyType<TLNexusNodeCreate[]>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createNodes>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Nodes
 */

export function useCreateNodes<
  TData = Awaited<ReturnType<typeof createNodes>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: CreateNodesPathParameters,
  tLNexusNodeCreate: BodyType<TLNexusNodeCreate[]>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createNodes>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateNodesQueryOptions({ projectId }, tLNexusNodeCreate, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Nodes
 */
export const prefetchCreateNodesQuery = async <
  TData = Awaited<ReturnType<typeof createNodes>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: CreateNodesPathParameters,
  tLNexusNodeCreate: BodyType<TLNexusNodeCreate[]>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createNodes>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateNodesQueryOptions({ projectId }, tLNexusNodeCreate, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Nodes
 */
export const invalidateCreateNodes = async (
  queryClient: QueryClient,
  { projectId }: CreateNodesPathParameters,
  tLNexusNodeCreate: BodyType<TLNexusNodeCreate[]>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateNodesQueryKey({ projectId }, tLNexusNodeCreate) }, options);

  return queryClient;
};

/**
 * @summary Delete Nexus Node
 */
export const deleteNexusNode = (
  { projectId, nodeId }: DeleteNexusNodePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>(
    { url: `/nexus/projects/${projectId}/graph/nodes/${nodeId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteNexusNodeQueryKey = ({ projectId, nodeId }: DeleteNexusNodePathParameters) => {
  return ['DELETE', 'nexus', 'projects', projectId, 'graph', 'nodes', nodeId] as const;
};

export const getDeleteNexusNodeQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteNexusNode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, nodeId }: DeleteNexusNodePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteNexusNode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteNexusNodeQueryKey({ projectId, nodeId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteNexusNode>>> = ({ signal }) =>
    deleteNexusNode({ projectId, nodeId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && nodeId !== null && nodeId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteNexusNode>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteNexusNodeQueryResult = NonNullable<Awaited<ReturnType<typeof deleteNexusNode>>>;
export type DeleteNexusNodeQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteNexusNode<
  TData = Awaited<ReturnType<typeof deleteNexusNode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteNexusNodePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteNexusNode>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteNexusNode>>,
          TError,
          Awaited<ReturnType<typeof deleteNexusNode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteNexusNode<
  TData = Awaited<ReturnType<typeof deleteNexusNode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteNexusNodePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteNexusNode>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteNexusNode>>,
          TError,
          Awaited<ReturnType<typeof deleteNexusNode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteNexusNode<
  TData = Awaited<ReturnType<typeof deleteNexusNode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteNexusNodePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteNexusNode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Nexus Node
 */

export function useDeleteNexusNode<
  TData = Awaited<ReturnType<typeof deleteNexusNode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, nodeId }: DeleteNexusNodePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteNexusNode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteNexusNodeQueryOptions({ projectId, nodeId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Nexus Node
 */
export const prefetchDeleteNexusNodeQuery = async <
  TData = Awaited<ReturnType<typeof deleteNexusNode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, nodeId }: DeleteNexusNodePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteNexusNode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteNexusNodeQueryOptions({ projectId, nodeId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Nexus Node
 */
export const invalidateDeleteNexusNode = async (
  queryClient: QueryClient,
  { projectId, nodeId }: DeleteNexusNodePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteNexusNodeQueryKey({ projectId, nodeId }) }, options);

  return queryClient;
};

/**
 * @summary Update Node
 */
export const updateNode = (
  { projectId, nodeId }: UpdateNodePathParameters,
  tLNexusNodeUpdate: BodyType<TLNexusNodeUpdate>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusNodeResponse>(
    {
      url: `/nexus/projects/${projectId}/graph/nodes/${nodeId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusNodeUpdate,
      signal,
    },
    options,
  );
};

export const getUpdateNodeQueryKey = (
  { projectId, nodeId }: UpdateNodePathParameters,
  tLNexusNodeUpdate?: BodyType<TLNexusNodeUpdate>,
) => {
  return ['PATCH', 'nexus', 'projects', projectId, 'graph', 'nodes', nodeId, tLNexusNodeUpdate] as const;
};

export const getUpdateNodeQueryOptions = <
  TData = Awaited<ReturnType<typeof updateNode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, nodeId }: UpdateNodePathParameters,
  tLNexusNodeUpdate: BodyType<TLNexusNodeUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateNode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateNodeQueryKey({ projectId, nodeId }, tLNexusNodeUpdate);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateNode>>> = ({ signal }) =>
    updateNode({ projectId, nodeId }, tLNexusNodeUpdate, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && nodeId !== null && nodeId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateNode>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateNodeQueryResult = NonNullable<Awaited<ReturnType<typeof updateNode>>>;
export type UpdateNodeQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateNode<
  TData = Awaited<ReturnType<typeof updateNode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateNodePathParameters,
  tLNexusNodeUpdate: BodyType<TLNexusNodeUpdate>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateNode>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateNode>>,
          TError,
          Awaited<ReturnType<typeof updateNode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateNode<
  TData = Awaited<ReturnType<typeof updateNode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateNodePathParameters,
  tLNexusNodeUpdate: BodyType<TLNexusNodeUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateNode>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateNode>>,
          TError,
          Awaited<ReturnType<typeof updateNode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateNode<
  TData = Awaited<ReturnType<typeof updateNode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateNodePathParameters,
  tLNexusNodeUpdate: BodyType<TLNexusNodeUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateNode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Node
 */

export function useUpdateNode<
  TData = Awaited<ReturnType<typeof updateNode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, nodeId }: UpdateNodePathParameters,
  tLNexusNodeUpdate: BodyType<TLNexusNodeUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateNode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateNodeQueryOptions({ projectId, nodeId }, tLNexusNodeUpdate, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Node
 */
export const prefetchUpdateNodeQuery = async <
  TData = Awaited<ReturnType<typeof updateNode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, nodeId }: UpdateNodePathParameters,
  tLNexusNodeUpdate: BodyType<TLNexusNodeUpdate>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateNode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateNodeQueryOptions({ projectId, nodeId }, tLNexusNodeUpdate, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Node
 */
export const invalidateUpdateNode = async (
  queryClient: QueryClient,
  { projectId, nodeId }: UpdateNodePathParameters,
  tLNexusNodeUpdate: BodyType<TLNexusNodeUpdate>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateNodeQueryKey({ projectId, nodeId }, tLNexusNodeUpdate) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Node Data
 */
export const getNodeData = (
  { projectId, nodeId }: GetNodeDataPathParameters,
  params?: GetNodeDataParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusNodeDataResponse>(
    { url: `/nexus/projects/${projectId}/graph/nodes/${nodeId}/data`, method: 'GET', params, signal },
    options,
  );
};

export const getGetNodeDataQueryKey = (
  { projectId, nodeId }: GetNodeDataPathParameters,
  params?: GetNodeDataParams,
) => {
  return ['nexus', 'projects', projectId, 'graph', 'nodes', nodeId, 'data', ...(params ? [params] : [])] as const;
};

export const getGetNodeDataQueryOptions = <
  TData = Awaited<ReturnType<typeof getNodeData>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, nodeId }: GetNodeDataPathParameters,
  params?: GetNodeDataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNodeData>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetNodeDataQueryKey({ projectId, nodeId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getNodeData>>> = ({ signal }) =>
    getNodeData({ projectId, nodeId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && nodeId !== null && nodeId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getNodeData>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetNodeDataQueryResult = NonNullable<Awaited<ReturnType<typeof getNodeData>>>;
export type GetNodeDataQueryError = ErrorType<TLHTTPValidationError>;

export function useGetNodeData<
  TData = Awaited<ReturnType<typeof getNodeData>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetNodeDataPathParameters,
  params: undefined | GetNodeDataParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNodeData>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNodeData>>,
          TError,
          Awaited<ReturnType<typeof getNodeData>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetNodeData<
  TData = Awaited<ReturnType<typeof getNodeData>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetNodeDataPathParameters,
  params?: GetNodeDataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNodeData>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNodeData>>,
          TError,
          Awaited<ReturnType<typeof getNodeData>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetNodeData<
  TData = Awaited<ReturnType<typeof getNodeData>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetNodeDataPathParameters,
  params?: GetNodeDataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNodeData>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Node Data
 */

export function useGetNodeData<
  TData = Awaited<ReturnType<typeof getNodeData>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, nodeId }: GetNodeDataPathParameters,
  params?: GetNodeDataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNodeData>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetNodeDataQueryOptions({ projectId, nodeId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Node Data
 */
export const prefetchGetNodeDataQuery = async <
  TData = Awaited<ReturnType<typeof getNodeData>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, nodeId }: GetNodeDataPathParameters,
  params?: GetNodeDataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNodeData>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetNodeDataQueryOptions({ projectId, nodeId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Node Data
 */
export const invalidateGetNodeData = async (
  queryClient: QueryClient,
  { projectId, nodeId }: GetNodeDataPathParameters,
  params?: GetNodeDataParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetNodeDataQueryKey({ projectId, nodeId }, params) }, options);

  return queryClient;
};

/**
 * @summary Delete Nexus Table Filter
 */
export const deleteNexusTableFilter = (
  { projectId, nodeId }: DeleteNexusTableFilterPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>(
    { url: `/nexus/projects/${projectId}/graph/nodes/${nodeId}/filter`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteNexusTableFilterQueryKey = ({ projectId, nodeId }: DeleteNexusTableFilterPathParameters) => {
  return ['DELETE', 'nexus', 'projects', projectId, 'graph', 'nodes', nodeId, 'filter'] as const;
};

export const getDeleteNexusTableFilterQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteNexusTableFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, nodeId }: DeleteNexusTableFilterPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteNexusTableFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteNexusTableFilterQueryKey({ projectId, nodeId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteNexusTableFilter>>> = ({ signal }) =>
    deleteNexusTableFilter({ projectId, nodeId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && nodeId !== null && nodeId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteNexusTableFilter>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteNexusTableFilterQueryResult = NonNullable<Awaited<ReturnType<typeof deleteNexusTableFilter>>>;
export type DeleteNexusTableFilterQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteNexusTableFilter<
  TData = Awaited<ReturnType<typeof deleteNexusTableFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteNexusTableFilterPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteNexusTableFilter>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteNexusTableFilter>>,
          TError,
          Awaited<ReturnType<typeof deleteNexusTableFilter>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteNexusTableFilter<
  TData = Awaited<ReturnType<typeof deleteNexusTableFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteNexusTableFilterPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteNexusTableFilter>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteNexusTableFilter>>,
          TError,
          Awaited<ReturnType<typeof deleteNexusTableFilter>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteNexusTableFilter<
  TData = Awaited<ReturnType<typeof deleteNexusTableFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteNexusTableFilterPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteNexusTableFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Nexus Table Filter
 */

export function useDeleteNexusTableFilter<
  TData = Awaited<ReturnType<typeof deleteNexusTableFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, nodeId }: DeleteNexusTableFilterPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteNexusTableFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteNexusTableFilterQueryOptions({ projectId, nodeId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Nexus Table Filter
 */
export const prefetchDeleteNexusTableFilterQuery = async <
  TData = Awaited<ReturnType<typeof deleteNexusTableFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, nodeId }: DeleteNexusTableFilterPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteNexusTableFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteNexusTableFilterQueryOptions({ projectId, nodeId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Nexus Table Filter
 */
export const invalidateDeleteNexusTableFilter = async (
  queryClient: QueryClient,
  { projectId, nodeId }: DeleteNexusTableFilterPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteNexusTableFilterQueryKey({ projectId, nodeId }) }, options);

  return queryClient;
};

/**
 * @summary Get Nexus Table Filter
 */
export const getNexusTableFilter = (
  { projectId, nodeId }: GetNexusTableFilterPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusTableFilterResponse>(
    { url: `/nexus/projects/${projectId}/graph/nodes/${nodeId}/filter`, method: 'GET', signal },
    options,
  );
};

export const getGetNexusTableFilterQueryKey = ({ projectId, nodeId }: GetNexusTableFilterPathParameters) => {
  return ['nexus', 'projects', projectId, 'graph', 'nodes', nodeId, 'filter'] as const;
};

export const getGetNexusTableFilterQueryOptions = <
  TData = Awaited<ReturnType<typeof getNexusTableFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, nodeId }: GetNexusTableFilterPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNexusTableFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetNexusTableFilterQueryKey({ projectId, nodeId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getNexusTableFilter>>> = ({ signal }) =>
    getNexusTableFilter({ projectId, nodeId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && nodeId !== null && nodeId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getNexusTableFilter>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetNexusTableFilterQueryResult = NonNullable<Awaited<ReturnType<typeof getNexusTableFilter>>>;
export type GetNexusTableFilterQueryError = ErrorType<TLHTTPValidationError>;

export function useGetNexusTableFilter<
  TData = Awaited<ReturnType<typeof getNexusTableFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetNexusTableFilterPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNexusTableFilter>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNexusTableFilter>>,
          TError,
          Awaited<ReturnType<typeof getNexusTableFilter>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetNexusTableFilter<
  TData = Awaited<ReturnType<typeof getNexusTableFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetNexusTableFilterPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNexusTableFilter>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNexusTableFilter>>,
          TError,
          Awaited<ReturnType<typeof getNexusTableFilter>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetNexusTableFilter<
  TData = Awaited<ReturnType<typeof getNexusTableFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetNexusTableFilterPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNexusTableFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Nexus Table Filter
 */

export function useGetNexusTableFilter<
  TData = Awaited<ReturnType<typeof getNexusTableFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, nodeId }: GetNexusTableFilterPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNexusTableFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetNexusTableFilterQueryOptions({ projectId, nodeId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Nexus Table Filter
 */
export const prefetchGetNexusTableFilterQuery = async <
  TData = Awaited<ReturnType<typeof getNexusTableFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, nodeId }: GetNexusTableFilterPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNexusTableFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetNexusTableFilterQueryOptions({ projectId, nodeId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Nexus Table Filter
 */
export const invalidateGetNexusTableFilter = async (
  queryClient: QueryClient,
  { projectId, nodeId }: GetNexusTableFilterPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetNexusTableFilterQueryKey({ projectId, nodeId }) }, options);

  return queryClient;
};

/**
 * @summary Set Nexus Table Filter
 */
export const setNexusTableFilter = (
  { projectId, nodeId }: SetNexusTableFilterPathParameters,
  tLNexusScopeChainInput: BodyType<TLNexusScopeChainInput>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusTableFilterResponse>(
    {
      url: `/nexus/projects/${projectId}/graph/nodes/${nodeId}/filter`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusScopeChainInput,
      signal,
    },
    options,
  );
};

export const getSetNexusTableFilterQueryKey = (
  { projectId, nodeId }: SetNexusTableFilterPathParameters,
  tLNexusScopeChainInput?: BodyType<TLNexusScopeChainInput>,
) => {
  return ['PUT', 'nexus', 'projects', projectId, 'graph', 'nodes', nodeId, 'filter', tLNexusScopeChainInput] as const;
};

export const getSetNexusTableFilterQueryOptions = <
  TData = Awaited<ReturnType<typeof setNexusTableFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, nodeId }: SetNexusTableFilterPathParameters,
  tLNexusScopeChainInput: BodyType<TLNexusScopeChainInput>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof setNexusTableFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getSetNexusTableFilterQueryKey({ projectId, nodeId }, tLNexusScopeChainInput);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof setNexusTableFilter>>> = ({ signal }) =>
    setNexusTableFilter({ projectId, nodeId }, tLNexusScopeChainInput, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && nodeId !== null && nodeId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof setNexusTableFilter>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type SetNexusTableFilterQueryResult = NonNullable<Awaited<ReturnType<typeof setNexusTableFilter>>>;
export type SetNexusTableFilterQueryError = ErrorType<TLHTTPValidationError>;

export function useSetNexusTableFilter<
  TData = Awaited<ReturnType<typeof setNexusTableFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SetNexusTableFilterPathParameters,
  tLNexusScopeChainInput: BodyType<TLNexusScopeChainInput>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof setNexusTableFilter>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof setNexusTableFilter>>,
          TError,
          Awaited<ReturnType<typeof setNexusTableFilter>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSetNexusTableFilter<
  TData = Awaited<ReturnType<typeof setNexusTableFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SetNexusTableFilterPathParameters,
  tLNexusScopeChainInput: BodyType<TLNexusScopeChainInput>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof setNexusTableFilter>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof setNexusTableFilter>>,
          TError,
          Awaited<ReturnType<typeof setNexusTableFilter>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSetNexusTableFilter<
  TData = Awaited<ReturnType<typeof setNexusTableFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SetNexusTableFilterPathParameters,
  tLNexusScopeChainInput: BodyType<TLNexusScopeChainInput>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof setNexusTableFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Set Nexus Table Filter
 */

export function useSetNexusTableFilter<
  TData = Awaited<ReturnType<typeof setNexusTableFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, nodeId }: SetNexusTableFilterPathParameters,
  tLNexusScopeChainInput: BodyType<TLNexusScopeChainInput>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof setNexusTableFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSetNexusTableFilterQueryOptions({ projectId, nodeId }, tLNexusScopeChainInput, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Set Nexus Table Filter
 */
export const prefetchSetNexusTableFilterQuery = async <
  TData = Awaited<ReturnType<typeof setNexusTableFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, nodeId }: SetNexusTableFilterPathParameters,
  tLNexusScopeChainInput: BodyType<TLNexusScopeChainInput>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof setNexusTableFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSetNexusTableFilterQueryOptions({ projectId, nodeId }, tLNexusScopeChainInput, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Set Nexus Table Filter
 */
export const invalidateSetNexusTableFilter = async (
  queryClient: QueryClient,
  { projectId, nodeId }: SetNexusTableFilterPathParameters,
  tLNexusScopeChainInput: BodyType<TLNexusScopeChainInput>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getSetNexusTableFilterQueryKey({ projectId, nodeId }, tLNexusScopeChainInput) },
    options,
  );

  return queryClient;
};

/**
 * @summary Remeasure Nexus Node Columns
 */
export const remeasureNexusNodeColumns = (
  { projectId, nodeId }: RemeasureNexusNodeColumnsPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusRemeasureResponse>(
    { url: `/nexus/projects/${projectId}/graph/nodes/${nodeId}/remeasure`, method: 'POST', signal },
    options,
  );
};

export const getRemeasureNexusNodeColumnsQueryKey = ({
  projectId,
  nodeId,
}: RemeasureNexusNodeColumnsPathParameters) => {
  return ['POST', 'nexus', 'projects', projectId, 'graph', 'nodes', nodeId, 'remeasure'] as const;
};

export const getRemeasureNexusNodeColumnsQueryOptions = <
  TData = Awaited<ReturnType<typeof remeasureNexusNodeColumns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, nodeId }: RemeasureNexusNodeColumnsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof remeasureNexusNodeColumns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRemeasureNexusNodeColumnsQueryKey({ projectId, nodeId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof remeasureNexusNodeColumns>>> = ({ signal }) =>
    remeasureNexusNodeColumns({ projectId, nodeId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && nodeId !== null && nodeId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof remeasureNexusNodeColumns>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RemeasureNexusNodeColumnsQueryResult = NonNullable<Awaited<ReturnType<typeof remeasureNexusNodeColumns>>>;
export type RemeasureNexusNodeColumnsQueryError = ErrorType<TLHTTPValidationError>;

export function useRemeasureNexusNodeColumns<
  TData = Awaited<ReturnType<typeof remeasureNexusNodeColumns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemeasureNexusNodeColumnsPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof remeasureNexusNodeColumns>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof remeasureNexusNodeColumns>>,
          TError,
          Awaited<ReturnType<typeof remeasureNexusNodeColumns>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemeasureNexusNodeColumns<
  TData = Awaited<ReturnType<typeof remeasureNexusNodeColumns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemeasureNexusNodeColumnsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof remeasureNexusNodeColumns>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof remeasureNexusNodeColumns>>,
          TError,
          Awaited<ReturnType<typeof remeasureNexusNodeColumns>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemeasureNexusNodeColumns<
  TData = Awaited<ReturnType<typeof remeasureNexusNodeColumns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemeasureNexusNodeColumnsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof remeasureNexusNodeColumns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Remeasure Nexus Node Columns
 */

export function useRemeasureNexusNodeColumns<
  TData = Awaited<ReturnType<typeof remeasureNexusNodeColumns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, nodeId }: RemeasureNexusNodeColumnsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof remeasureNexusNodeColumns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRemeasureNexusNodeColumnsQueryOptions({ projectId, nodeId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Remeasure Nexus Node Columns
 */
export const prefetchRemeasureNexusNodeColumnsQuery = async <
  TData = Awaited<ReturnType<typeof remeasureNexusNodeColumns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, nodeId }: RemeasureNexusNodeColumnsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof remeasureNexusNodeColumns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRemeasureNexusNodeColumnsQueryOptions({ projectId, nodeId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Remeasure Nexus Node Columns
 */
export const invalidateRemeasureNexusNodeColumns = async (
  queryClient: QueryClient,
  { projectId, nodeId }: RemeasureNexusNodeColumnsPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRemeasureNexusNodeColumnsQueryKey({ projectId, nodeId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Import Schema
 */
export const importSchema = (
  { projectId }: ImportSchemaPathParameters,
  tLImportSchemaNexusProjectsGraphRequest: BodyType<TLImportSchemaNexusProjectsGraphRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  const formData = new FormData();
  formData.append(`file`, tLImportSchemaNexusProjectsGraphRequest.file);
  if (
    tLImportSchemaNexusProjectsGraphRequest.system_name !== undefined &&
    tLImportSchemaNexusProjectsGraphRequest.system_name !== null
  ) {
    formData.append(`system_name`, tLImportSchemaNexusProjectsGraphRequest.system_name);
  }

  return apiInstance<TLNexusSchemaImportResponse>(
    {
      url: `/nexus/projects/${projectId}/graph/schema/import`,
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      signal,
    },
    options,
  );
};

export const getImportSchemaQueryKey = (
  { projectId }: ImportSchemaPathParameters,
  tLImportSchemaNexusProjectsGraphRequest?: BodyType<TLImportSchemaNexusProjectsGraphRequest>,
) => {
  return [
    'POST',
    'nexus',
    'projects',
    projectId,
    'graph',
    'schema',
    'import',
    tLImportSchemaNexusProjectsGraphRequest,
  ] as const;
};

export const getImportSchemaQueryOptions = <
  TData = Awaited<ReturnType<typeof importSchema>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: ImportSchemaPathParameters,
  tLImportSchemaNexusProjectsGraphRequest: BodyType<TLImportSchemaNexusProjectsGraphRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importSchema>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getImportSchemaQueryKey({ projectId }, tLImportSchemaNexusProjectsGraphRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof importSchema>>> = ({ signal }) =>
    importSchema({ projectId }, tLImportSchemaNexusProjectsGraphRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof importSchema>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ImportSchemaQueryResult = NonNullable<Awaited<ReturnType<typeof importSchema>>>;
export type ImportSchemaQueryError = ErrorType<TLHTTPValidationError>;

export function useImportSchema<
  TData = Awaited<ReturnType<typeof importSchema>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ImportSchemaPathParameters,
  tLImportSchemaNexusProjectsGraphRequest: BodyType<TLImportSchemaNexusProjectsGraphRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof importSchema>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof importSchema>>,
          TError,
          Awaited<ReturnType<typeof importSchema>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useImportSchema<
  TData = Awaited<ReturnType<typeof importSchema>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ImportSchemaPathParameters,
  tLImportSchemaNexusProjectsGraphRequest: BodyType<TLImportSchemaNexusProjectsGraphRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importSchema>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof importSchema>>,
          TError,
          Awaited<ReturnType<typeof importSchema>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useImportSchema<
  TData = Awaited<ReturnType<typeof importSchema>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ImportSchemaPathParameters,
  tLImportSchemaNexusProjectsGraphRequest: BodyType<TLImportSchemaNexusProjectsGraphRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importSchema>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Import Schema
 */

export function useImportSchema<
  TData = Awaited<ReturnType<typeof importSchema>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: ImportSchemaPathParameters,
  tLImportSchemaNexusProjectsGraphRequest: BodyType<TLImportSchemaNexusProjectsGraphRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importSchema>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getImportSchemaQueryOptions({ projectId }, tLImportSchemaNexusProjectsGraphRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Import Schema
 */
export const prefetchImportSchemaQuery = async <
  TData = Awaited<ReturnType<typeof importSchema>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: ImportSchemaPathParameters,
  tLImportSchemaNexusProjectsGraphRequest: BodyType<TLImportSchemaNexusProjectsGraphRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importSchema>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getImportSchemaQueryOptions({ projectId }, tLImportSchemaNexusProjectsGraphRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Import Schema
 */
export const invalidateImportSchema = async (
  queryClient: QueryClient,
  { projectId }: ImportSchemaPathParameters,
  tLImportSchemaNexusProjectsGraphRequest: BodyType<TLImportSchemaNexusProjectsGraphRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getImportSchemaQueryKey({ projectId }, tLImportSchemaNexusProjectsGraphRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Schema Import Template
 */
export const getSchemaImportTemplate = (
  { projectId }: GetSchemaImportTemplatePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusSchemaImportTemplate>(
    { url: `/nexus/projects/${projectId}/graph/schema/import/template`, method: 'GET', signal },
    options,
  );
};

export const getGetSchemaImportTemplateQueryKey = ({ projectId }: GetSchemaImportTemplatePathParameters) => {
  return ['nexus', 'projects', projectId, 'graph', 'schema', 'import', 'template'] as const;
};

export const getGetSchemaImportTemplateQueryOptions = <
  TData = Awaited<ReturnType<typeof getSchemaImportTemplate>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: GetSchemaImportTemplatePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSchemaImportTemplate>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSchemaImportTemplateQueryKey({ projectId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSchemaImportTemplate>>> = ({ signal }) =>
    getSchemaImportTemplate({ projectId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getSchemaImportTemplate>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetSchemaImportTemplateQueryResult = NonNullable<Awaited<ReturnType<typeof getSchemaImportTemplate>>>;
export type GetSchemaImportTemplateQueryError = ErrorType<TLHTTPValidationError>;

export function useGetSchemaImportTemplate<
  TData = Awaited<ReturnType<typeof getSchemaImportTemplate>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetSchemaImportTemplatePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSchemaImportTemplate>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSchemaImportTemplate>>,
          TError,
          Awaited<ReturnType<typeof getSchemaImportTemplate>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSchemaImportTemplate<
  TData = Awaited<ReturnType<typeof getSchemaImportTemplate>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetSchemaImportTemplatePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSchemaImportTemplate>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSchemaImportTemplate>>,
          TError,
          Awaited<ReturnType<typeof getSchemaImportTemplate>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSchemaImportTemplate<
  TData = Awaited<ReturnType<typeof getSchemaImportTemplate>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetSchemaImportTemplatePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSchemaImportTemplate>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Schema Import Template
 */

export function useGetSchemaImportTemplate<
  TData = Awaited<ReturnType<typeof getSchemaImportTemplate>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: GetSchemaImportTemplatePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSchemaImportTemplate>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetSchemaImportTemplateQueryOptions({ projectId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Schema Import Template
 */
export const prefetchGetSchemaImportTemplateQuery = async <
  TData = Awaited<ReturnType<typeof getSchemaImportTemplate>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: GetSchemaImportTemplatePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSchemaImportTemplate>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetSchemaImportTemplateQueryOptions({ projectId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Schema Import Template
 */
export const invalidateGetSchemaImportTemplate = async (
  queryClient: QueryClient,
  { projectId }: GetSchemaImportTemplatePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetSchemaImportTemplateQueryKey({ projectId }) }, options);

  return queryClient;
};
